import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { CopilotService } from '@helios/copilot'
import { ErrorLogModalComponent } from '@helios/error-log'
import { HotkeysHelpService } from '@helios/hotkeys'
import { isLayoutLocked, LayoutManagerComponent } from '@helios/layout-manager'
import { PanelConfig } from '@helios/panel-base'
import { AccountDetailsKey } from '@helios/panels/account-details'
import { AccountListKey } from '@helios/panels/account-list'
import { BulkClearwaterTransactionsKey } from '@helios/panels/bulk-clearwater-transactions'
import { BulkExternalTransactionsKey } from '@helios/panels/bulk-external-transactions'
import { ClearwaterTransactionsKey } from '@helios/panels/clearwater-transactions'
import { CorporateActionsKey } from '@helios/panels/corporate-actions'
import { DevtoolsKey } from '@helios/panels/devtools'
import { DistributionsKey } from '@helios/panels/distributions'
import { ExternalAccountsPanelKey } from '@helios/panels/external-accounts'
import { ExternalFileManagerKey } from '@helios/panels/external-file-manager'
import { ExternalFileViewerKey } from '@helios/panels/external-file-viewer'
import { ExternalTransactionsKey } from '@helios/panels/external-transactions'
import { GettingStartedPanelKey } from '@helios/panels/getting-started'
import { JobsKey } from '@helios/panels/jobs'
import { MultiBreaksKey } from '@helios/panels/multi-breaks'
import { PositionsKey } from '@helios/panels/positions'
import { PricingKey } from '@helios/panels/pricing'
import { SecurityWebComponentKey } from '@helios/panels/security-web-component'
import { TransactionDetailKey } from '@helios/panels/transaction-detail'
import { TransactionsKey } from '@helios/panels/transactions'
import {
  JobCompletionService,
  JobFailureService,
  SessionsService,
} from '@helios/sessions'
import {
  clearCommentaryActivity,
  clearCommentaryCounts,
  destroyStack,
  focusPanelAction,
  getSessionTitle,
  PeriodLength,
  periodLengthToDate,
  loadBreaksAction,
  loadCommentaryAccountCounts,
  loadCommentaryActivity,
  loadCommentaryCounts,
  MyAccountInfo,
  openCommentaryDrawer,
  RootState,
  selectActualReconDate,
  selectActiveSession,
  selectActiveWorkspace,
  selectCommandsIsLoading,
  selectCommentaryActivityCount,
  selectCurrentUser,
  selectErrorLogSize,
  selectFocusedCommentaryAccountId,
  selectFocusedMyAccount,
  selectIsCreatingSession,
  selectSessions,
  selectUserPolicy,
  SessionWithState,
  setFocusedCWTransactions,
  setFocusedLegacyOrigIds,
  setFocusedLegacyTranIds,
  setFocusedUIComponent,
  StackToPanelKey,
  updateActiveLayout,
  updateStack,
  User,
  userHasCommentaryAccess,
  userHasNotificationsAccess,
  UserPolicy,
  Workspace,
} from '@helios/state-management'
import { CommentaryEntityContext, HttpAccountService } from '@helios/common-api'
import {
  CommentaryDeepLinkResult,
  CommentaryFollowService,
  CommentaryFollowTarget,
  CommentaryLiveAccountsService,
  CommentsDrawerFrameComponent,
  readCommentaryShadowEvent,
} from '@helios/comments'
import { panelKeysForEntityType } from './commentary-entity-panel-map'
import { TelemetryService, UsageReportService } from '@helios/telemetry'
import {
  ModalPositionService,
  UserPreferencesService,
  WorkspaceStoreLoaderService,
  WorkspaceUpdateListenerService,
} from '@helios/user-preferences'
import { compareSets, Deferred, getZeroUtcDateAsDate } from '@helios/utils'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { PanelComponent } from '@northfork/ng-panel'
import { environment } from 'environments/environment'
import { LayoutConfig, ResolvedLayoutConfig } from 'golden-layout'
import _ from 'lodash-es'
import { ActiveToast, ToastrService } from 'ngx-toastr'
import { BehaviorSubject, merge, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  scan,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators'
import { addCopilotButton, loadCopilot } from './external-components/copilot'
import { setupCopilotResizing } from './external-components/copilot-resize'
import { loadCommentaryScript } from './external-components/commentary'
import {
  CommentLink,
  loadNotificationUiScript,
} from './external-components/notification-ui'
import { loadOutputManagerScript } from './external-components/output-manager'
import { loadRawTradeSummaryScript } from './external-components/raw-trade-summary'
import { loadReconDashboardScript } from './external-components/recon-dashboard'
import { loadSecurityDetailsScript } from './external-components/security-details'
import { loadSecurityMappingScript } from './external-components/security-mapping'
import { loadSmartSuspenseScript } from './external-components/smart-suspense'
import { loadClarityOpsVisualizerScript } from './external-components/clarity-ops-visualizer'
import { getAvailablePanels, getExternalPanels } from './helios-panels'
import { HeliosHotkeysService } from './hotkeys/helios-hotkeys.service'
import { paths } from './paths'
import { GlobalEventsService } from './services/global-events.service'
import { HeliosMessagingService } from './services/helios-messaging/helios-messaging.service'
import { InactiveService } from './services/inactive.service'
import { SessionCacheStatusService } from './services/session-cache-status.service'
import { SessionCacheTimerService } from './services/session-cache-timer.service'
import { SessionDataAvailabilityService } from './services/session-data-availability.service'

/* Declaring "iph" function in typescript for ZoomIn widget */
export declare let iph: (arg0?: string, arg1?: string) => void

const RECON_DASHBOARD_CW_TRANSACTION_EVENT =
  'recon-dashboard:ClearwaterTransaction'

/** Caps subaccount ids rolled up per aggregate — commentary-ui fans out one GET plus one
 *  WebSocket per related id. Its own `MAX_RELATED_ACCOUNT_IDS` caps the same list at 50,
 *  so the two must move together. */
const MAX_COMMENTARY_RELATED_ACCOUNT_IDS = 50

// Suffix must equal recon-dashboard-ws's COMMENTS_FILTER_DATA_TYPE exactly.
const RECON_DASHBOARD_COMMENT_THREAD_EVENT = 'recon-dashboard:CommentThread'

/* THESE ARE ZOOM IN WIDGET MAPPINGS FOR EACH PANEL, SO CAREFULLY CHANGE IT */
const zoomInProductHelpMappings = new Map([
  [GettingStartedPanelKey, 'helios-getting-started-panel'],
  [AccountListKey, 'helios-accounts-panel'],
  [AccountDetailsKey, 'helios-account-detail-panel'],
  [TransactionsKey, 'helios-transactions-panel'],
  [TransactionDetailKey, 'helios-transaction-details-panel'],
  [JobsKey, 'helios-jobs-panel'],
  [PositionsKey, 'helios-positions-panel'],
  [PricingKey, 'helios-pricing-panel'],
  [MultiBreaksKey, 'helios-break-and-variance-panel'],
  [DistributionsKey, 'helios-distributions-panel'],
  [CorporateActionsKey, 'helios-corporate-actions-panel'],
  [SecurityWebComponentKey, 'helios-security-details-panel'],
  [ExternalFileViewerKey, 'helios-external-file-viewer-panel'],
  [ExternalFileManagerKey, 'helios-external-file-manager-panel'],
  [ExternalTransactionsKey, 'helios-external-transactions-panel'],
  [ClearwaterTransactionsKey, 'helios-cw-transactions-panel'],
  [BulkClearwaterTransactionsKey, 'helios-bulk-cw-transactions-panel'],
  [BulkExternalTransactionsKey, 'helios-bulk-external-transactions-panel'],
  [DevtoolsKey, ''],

  /* Navigation Buttons */
  ['PanelCatalog', 'helios-panel-buttons'],
  ['SessionsDrawer', 'helios-session-panels'],
  ['WorkspaceCatalog', 'helios-workspace-panels'],
  ['CommandsDrawer', 'helios-command-panels'],

  /* ALSO ADD MORE NEW PANELS KEY, AND CONFIGURE THEM IN ZOOMIN HELP WIDGET ADMIN CONSOLE */
])

type HeliosEndNavContent =
  | 'PanelCatalog'
  | 'SessionsDrawer'
  | 'WorkspaceCatalog'
  | 'CommandsDrawer'
  | 'UserPreferences'

type LoadTimingStage =
  | 'appComponentStart'
  | 'userLoginComplete'
  | 'userPolicyComplete'
  | 'initWorkspacesComplete'
  | 'initComplete'

@Component({
  selector: 'hs-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly COMMENTARY_DEEP_LINK_TIMEOUT_MS = 10000
  readonly dockerTag = environment.dockerTag
  isProd = environment.production
  title = 'helios'
  panels: PanelConfig[] = []

  // used for automated testing and load time tracking.
  // this sets a property on the html so that cypress can know when things are ready so it can proceed.
  isInitialized = false
  //we can't send telemetry until the user logs in, so we'll just collect times here and send them all at the end
  loadTimingEvents = new Array<{
    loadStage: LoadTimingStage
    durationMs: number
  }>()

  private readonly _panelIndex = new Map<string, PanelConfig>()

  lockLayout = false
  private readonly layoutLockedSubject = new BehaviorSubject<boolean>(
    this.lockLayout,
  )
  layoutLocked = this.layoutLockedSubject.pipe(distinctUntilChanged())
  lockLayoutDisabled = false

  commandsDisabled = false
  commandCount = 0
  commandsLoading = false

  loadedSessionDisplay = ''

  layout?: LayoutConfig

  @ViewChild('heliosEndNav')
  readonly heliosEndNav?: PanelComponent
  @ViewChild('copilot', { read: ElementRef })
  readonly copilot?: ElementRef<HTMLElement>
  @ViewChild('commentsDrawerFrame')
  readonly commentsDrawerFrame?: CommentsDrawerFrameComponent

  heliosEndNavContent?: HeliosEndNavContent
  toggleSideNavComplete = true

  hasTimedOutCommands = false
  isFocusMode = false
  errorLogSize = 0
  sessionsCount = 0

  isCreatingSession = false
  activeSession?: SessionWithState
  isZoomInHelpActive = false
  hasAccessCommentary = false
  hasAccessNotifications = false
  // DLITE-3912: account chip-follow routes by panel ACCESS — Accounts when the
  // user can access it, otherwise My Accounts (external-accounts). Captured
  // from the user policy in onUserLogin and read by the follow$ handler.
  private hasAccountsPanelAccess = false
  private hasMyAccountsPanelAccess = false
  commentaryEntityContext: CommentaryEntityContext | null = null
  commentaryAction: 'view' | 'add' | null = null
  commentaryDrawerOpen = false
  commentaryAccountId: string | null = null
  /** DLITE-5094: registry owner keys for the live thread-event subscriptions
   *  of the active session's accounts and of the drawer's account. */
  private readonly liveSessionOwner = { owner: 'session' }
  private readonly liveDrawerOwner = { owner: 'drawer' }
  /**
   * Account a followed chip is loading while the drawer sits on another one (an
   * aggregate's rollup); its selection/session echo must not repoint the drawer.
   * Released once the session attempt settles.
   */
  private commentaryFollowPin: string | null = null
  /** JSON-encoded string[] of commentaryAccountId's underlying simple-account
   *  ids (null if none), so the drawer can roll up their threads too. */
  commentaryRelatedAccountIds: string | null = null
  /**
   * The account commentaryRelatedAccountIds was last successfully resolved
   * for. Tracked separately because a simple account resolves to null, so the
   * payload alone cannot distinguish "not resolved yet" from "resolved, has
   * none" — inferring it from the payload re-ran the fetch on every reopen of
   * any account without subaccounts. Left null on a failed resolve so the next
   * open retries.
   */
  private commentaryRelatedAccountsResolvedFor: string | null = null
  /**
   * DLITE-4369 — the specific comment to jump to, set only by a shared
   * comment link. Assigned by the openCommentaryDrawer subscriber on every
   * dispatch (null when the payload carries none), so a stale id can't
   * survive into an unrelated later open; also cleared alongside
   * commentaryEntityContext by clearCommentaryContext for paths that don't
   * dispatch that action.
   */
  commentaryCommentId: string | null = null
  // PVAL-26203. Cleared alongside commentaryEntityContext/commentaryCommentId
  // by clearCommentaryContext (which is why a widget-narrowed slice — no
  // entityContext — never reaches onPanelFocused's clear condition, but a
  // deep-linked thread — entityContext set — does, matching commentaryCommentId).
  // Also reset by setCommentaryAccount on a real account switch. Both of
  // those respect commentaryDeepLinkPending, same as clearCommentaryContext
  // itself, so an ambient account/panel change can't silently un-pin a
  // cold-opened deep link before it resolves. Every openCommentaryDrawer
  // dispatch that sends no ids (grid bubble, context menu, smart suspense)
  // also resets the pin on the way in via the unconditional assign in the
  // subscriber further below.
  commentaryThreadIds: string[] | null = null
  /** Bumped whenever an openCommentaryDrawer dispatch carries thread ids, so a repeat click on the same slice is still observable to <commentary-ui>. */
  commentaryThreadIdsSeq = 0
  /**
   * Bumped on every "Add Comment" dispatch. Lets <commentary-ui> tell a real
   * click apart from navigation, which also changes the row context while
   * `commentaryAction` stays "add" — and see a repeat click on the same row.
   */
  commentaryActionSeq = 0
  /**
   * DLITE-4369 — true from the moment a shared comment link is detected
   * until commentary-ui reports the deep-linked thread is open or failed.
   * Drives `isRevealed` on hs-comments-drawer-frame so the drawer chrome
   * stays hidden while it does background work, instead of flashing an
   * empty/loading drawer at the user.
   */
  commentaryDeepLinkPending = false
  private _commentaryDeepLinkToken = 0
  private _commentaryDeepLinkTimeout: ReturnType<typeof setTimeout> | null =
    null
  private _commentaryDeepLinkToast: ActiveToast<unknown> | null = null
  // DLITE-4032: the currently-focused panel, and the panel the open commentary
  // context was opened from. A focus change to a DIFFERENT panel (onPanelFocused)
  // or a session account change clears the pinned context so a new thread can't
  // inherit a stale entity link from a prior panel/account.
  private _focusedPanelKey: string | null = null
  private _commentaryContextPanelKey: string | null = null
  /**
   * Must equal the author id commentary-ws stores on comments (the legacy user id): the drawer
   * matches on it to offer editing and to judge unread. Also sent as `X-Dev-User` in dev.
   */
  commentaryUserId: string | null = null
  /** Display name for `X-User-Display`; display-only, not authoritative for attribution. */
  commentaryUserDisplayName: string | null = null
  commentActivityCount = 0

  env =
    environment.environment === 'dev'
      ? 'Dev'
      : environment.environment === 'staging'
      ? 'Staging'
      : ''

  region =
    environment.realm === 'stg2' || environment.realm === 'prd2' ? 'EU' : ''

  /**
   * @internal for testing
   */
  @ViewChild('layoutManager')
  readonly layoutManagerComponent?: LayoutManagerComponent

  private readonly unRegisterGeneralEventsService: () => void
  private readonly _destroyed$ = new Subject<void>()
  private readonly _dragSourcesCreatedDfd = new Deferred<void>()

  private readonly _onReconDashboardBreakEvent = (event: Event) => {
    const customEvent = event as CustomEvent<{
      pointData?: {
        ids?: string[]
      }
    }>

    const ids = customEvent?.detail?.pointData?.ids
    if (!Array.isArray(ids) || ids.length === 0) return

    this.store.dispatch(loadBreaksAction({ type: 'breakIds', ids: ids }))
  }

  /**
   * `category` / `subCategory` are the backend's filterKey — deliberately not
   * read, so chart labels never become a frontend contract.
   */
  private readonly _onReconDashboardCommentThreadEvent = (event: Event) => {
    const customEvent = event as CustomEvent<{
      pointData?: {
        ids?: string[]
        accountId?: number
      }
    }>

    // Non-strings reach commentary-ui, which drops them until the filter
    // disarms itself and silently shows the whole account.
    const raw = customEvent?.detail?.pointData?.ids
    if (!Array.isArray(raw) || raw.length === 0) return
    const ids = raw.filter(
      (v): v is string => typeof v === 'string' && v.trim() !== '',
    )
    // A partially-unusable list must not silently narrow to the surviving
    // subset — the widget promised every id in raw, so showing fewer with no
    // indication looks like wrong data, not degraded input. Either the slice
    // is exact or the click is a no-op.
    if (ids.length !== raw.length) return

    // A widget click is a new open gesture; it must supersede an in-flight
    // deep link rather than open a hidden drawer behind it (isRevealed stays
    // false while commentaryDeepLinkPending is true) and risk that deep
    // link's eventual resolution/timeout closing the drawer the user just
    // opened.
    this.cancelPendingCommentaryDeepLink()

    const accountId = customEvent.detail?.pointData?.accountId
    if (accountId != null) this.setCommentaryAccount(String(accountId))

    this.store.dispatch(
      openCommentaryDrawer({
        entityContext: null,
        commentaryAction: 'view',
        threadIds: ids,
      }),
    )
  }

  private readonly _onReconDashboardClearwaterTransactionEvent = async (
    event: Event,
  ) => {
    const customEvent = event as CustomEvent<{
      pointData?: {
        ids?: string[]
      }
    }>

    const ids = customEvent?.detail?.pointData?.ids
    if (!Array.isArray(ids) || ids.length === 0) return

    const account = await this.store
      .select(selectFocusedMyAccount)
      .pipe(take(1))
      .toPromise()
    if (!account) return

    // Checks loadedAccountIds only (not simpleAccountIds / isAccountInFocusedSession),
    // which is sufficient here because the focused account is exactly what create() loads.
    const activeSession = await this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .toPromise()
    const alreadyLoaded = !!activeSession?.loadedAccountIds?.includes(
      account.accountId,
    )

    if (!alreadyLoaded) {
      const reconDate = await this.store
        .select(selectActualReconDate)
        .pipe(take(1))
        .toPromise()

      // overrideAutoclose=false (the default Create-New-Session path): runs the aggregate
      // guard (toasts + returns undefined for unsupported types), autoclose, session-limit
      // check, and rejoin-existing-session logic — not only the aggregate guard.
      const session = await this.sessionsService.create(
        [account.accountId],
        reconDate ?? new Date(),
        periodLengthToDate(PeriodLength.Last100Days),
        false,
      )
      if (!session) return
    }

    // No focusPanel here — TransactionFocusEffects focuses the transactions
    // panel in response to setFocusedCWTransactions.
    this.store.dispatch(setFocusedCWTransactions(ids))
  }

  constructor(
    generalEventsService: GlobalEventsService,
    documentTitle: Title,
    private readonly store: Store<RootState>,
    private readonly actions$: Actions,
    readonly heliosHotkeysService: HeliosHotkeysService,
    public hotkeysHelpService: HotkeysHelpService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sessionsService: SessionsService,
    private readonly sessionCacheStatusService: SessionCacheStatusService,
    private readonly sessionCacheTimerService: SessionCacheTimerService,
    private readonly sessionDataAvailabilityService: SessionDataAvailabilityService,
    private readonly telemetryService: TelemetryService,
    private readonly usageReportService: UsageReportService,
    public messaging: HeliosMessagingService,
    private readonly preferences: UserPreferencesService,
    jobCompletionService: JobCompletionService,
    private readonly jobFailureService: JobFailureService,
    private readonly loaderService: WorkspaceStoreLoaderService,
    private readonly listenerService: WorkspaceUpdateListenerService,
    @Inject(DOCUMENT) private readonly _document: Document,
    readonly modalService: NgbModal,
    private readonly inactiveService: InactiveService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly copilotService: CopilotService,
    readonly modalPositionService: ModalPositionService,
    private readonly followService: CommentaryFollowService,
    private readonly httpAccountService: HttpAccountService,
    private readonly toastrService: ToastrService,
    private readonly liveAccounts: CommentaryLiveAccountsService,
  ) {
    this.addLoadTimingEvent('appComponentStart')
    this.inactiveService.init()
    this.unRegisterGeneralEventsService = generalEventsService.initialize()
    sessionCacheStatusService.initialize()
    sessionCacheTimerService.initialize()
    sessionDataAvailabilityService.initialize()

    // wait for user login to complete
    store
      .select(selectCurrentUser)
      .pipe(
        takeWhile(user => user == null, true),
        filter((user): user is User => user != null),
        take(1),
        tap(user => this.onUserLogin(user)),
      )
      .subscribe()

    store
      .select(selectCurrentUser)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(user => {
        this.commentaryUserId =
          user?.clearwaterData?.legacyUserId?.toString() ||
          user?.email ||
          user?.nickname ||
          user?.auth0Id ||
          null
        this.commentaryUserDisplayName =
          user?.clearwaterData?.name ||
          user?.name ||
          user?.nickname ||
          user?.email ||
          null
      })

    // manage the page title, based on account id
    documentTitle.setTitle('Helios')
    this.store
      .select(selectActiveSession)
      .pipe(
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
        tap(session => {
          this.activeSession = session
          const sessionTitle = getSessionTitle(session)
          let title: string
          if (session != null) {
            title = `${sessionTitle} - Helios`

            this.commandCount = session.sumOfCommands ?? 0
          } else {
            title = 'Helios'
            this.loadedSessionDisplay = ''
            this.commandCount = 0
          }
          documentTitle.setTitle(title)
        }),
      )
      .subscribe()

    // DLITE-4110 — commentary no longer requires a session. The drawer's
    // account and the header activity badge follow the account the user most
    // recently picked, falling back to the active session's loaded account.
    // (The within-account entity counts that back the Transactions / Positions
    // / Breaks columns are NOT driven from here — they follow the SESSION
    // account, wired separately below, because those panels always show session
    // data even when the drawer is pointed at a different selected account.)
    // `merge` gives "last interaction wins":
    //   - single-click an account (Accounts panel)  -> selectFocusedCommentaryAccountId
    //   - double-click a My Account                  -> selectFocusedMyAccount
    //   - activate / close a session                 -> selectActiveSession
    // commentary-ui and the counts endpoint are single-account, so we scope to
    // one accountId. A double-click on an aggregate (My Accounts) points the
    // drawer at the aggregate's own accountId, the same as a simple — for now
    // it shows that account's own threads (empty until we decide how to roll up
    // the child simples' comments; see the aggregate roll-up follow-up).
    //
    // "Last interaction wins" with one exception: a deactivated session emits
    // null, but that is a teardown, NOT the user choosing to view "nothing". If
    // the user selected another account AFTER activating the session, a
    // session-null must defer to that still-valid selection rather than blanking
    // the drawer. `scan` tracks the last explicit selection so session teardown
    // reverts to it; when there was no later selection (the drawer was following
    // the session) it clears, as before. A null from selection is filtered out
    // so closing/deselecting a panel doesn't wipe comments.
    //
    // NOTE: "last selection" here means ONLY an Accounts-panel single-click
    // (selectFocusedCommentaryAccountId) or a My-Accounts double-click
    // (selectFocusedMyAccount) — the two streams merged below. An account
    // adopted by opening a comment bubble / context menu does NOT feed this
    // stream (openCommentaryDrawer sets commentaryAccountId directly), so a
    // session teardown reverts to the last PANEL selection (or null), which may
    // differ from a bubble-adopted account. That's an accepted edge — closing a
    // session is a defensible reset.
    merge(
      merge(
        this.store
          .select(selectFocusedCommentaryAccountId)
          .pipe(filter((accountId): accountId is number => accountId != null)),
        this.store.select(selectFocusedMyAccount).pipe(
          filter((account): account is MyAccountInfo => account != null),
          map(account => account.accountId),
        ),
      ).pipe(map(id => ({ kind: 'selection' as const, id: String(id) }))),
      this.store.select(selectActiveSession).pipe(
        map(session => ({
          kind: 'session' as const,
          id:
            session?.loadedAccountIds?.[0] != null
              ? String(session.loadedAccountIds[0])
              : null,
        })),
      ),
    )
      .pipe(
        scan<
          { kind: 'selection' | 'session'; id: string | null },
          { lastSelection: string | null; account: string | null }
        >(
          (state, evt) => {
            if (evt.kind === 'selection') {
              // An explicit account selection wins and is remembered.
              return { lastSelection: evt.id, account: evt.id }
            }
            if (evt.id != null) {
              // Session activated / switched → it wins for now, but the last
              // selection is preserved so a later teardown can revert to it.
              return { ...state, account: evt.id }
            }
            // Session deactivated → revert to a still-valid selection (or null).
            return { ...state, account: state.lastSelection }
          },
          { lastSelection: null, account: null },
        ),
        map(state => state.account),
        // Before distinctUntilChanged so the ignored echo isn't remembered as
        // the last value; selecting that account later must still get through.
        filter(
          accountId =>
            accountId == null || accountId !== this.commentaryFollowPin,
        ),
        distinctUntilChanged(),
        takeUntil(this._destroyed$),
      )
      .subscribe(accountId => {
        this.setCommentaryAccount(accountId)
        // DLITE-4110: navigating to a different account means the user moved on,
        // so drop any pinned entity ("This Row") — otherwise it stays stuck on
        // the previously selected row while the Inbox re-points to the new
        // account, and only a drawer close/reopen resets it. The comment-bubble
        // / context-menu path sets the pin directly (openCommentaryDrawer) and
        // does NOT feed this merge, so this only fires on genuine account
        // navigation and never wipes a pin the user just opened from a row.
        this.clearCommentaryContext()
      })

    // DLITE-4112 — within-account entity counts (transactionId / assetId /
    // breakId keys feeding the Transactions / Positions / Breaks columns) are
    // loaded per-account by each entity grid for the rows it displays (session
    // or not — breaks can load without a session). When the session's loaded
    // account changes we clear the per-account entity maps so stale counts from
    // a previous context don't linger; the grids reload for the new context on
    // their next render.
    this.store
      .select(selectActiveSession)
      .pipe(
        map(session => session?.loadedAccountIds?.[0] ?? null),
        distinctUntilChanged(),
        takeUntil(this._destroyed$),
      )
      .subscribe(accountId => {
        // DLITE-4032: a session ACCOUNT change (incl. deactivation) means the
        // user moved on — reset any pinned commentary entity so a new thread
        // links to the new selection, not a stale one. distinctUntilChanged on
        // loadedAccountIds[0] means benign in-session updates don't fire this.
        if (accountId == null || String(accountId) !== this.commentaryFollowPin)
          this.clearCommentaryContext()
        if (this.hasAccessCommentary) {
          this.store.dispatch(clearCommentaryCounts())
        }
      })

    // DLITE-5094 — the active session's accounts are displayed (entity badges),
    // so keep them subscribed on the thread-event socket; released when the
    // session goes away. Held by the service until start() (access-gated).
    this.store
      .select(selectActiveSession)
      .pipe(
        map(session =>
          (session?.loadedAccountIds ?? [])
            .filter(id => id != null)
            .map(id => String(id)),
        ),
        distinctUntilChanged(
          (a, b) => a.length === b.length && a.every((id, i) => id === b[i]),
        ),
        takeUntil(this._destroyed$),
      )
      .subscribe(accountIds => {
        if (accountIds.length > 0) {
          this.liveAccounts.setOwner(
            this.liveSessionOwner,
            'entity',
            accountIds,
          )
        } else {
          this.liveAccounts.releaseOwner(this.liveSessionOwner)
        }
      })

    this.actions$
      .pipe(takeUntil(this._destroyed$), ofType(focusPanelAction))
      .subscribe(action => this.focusPanel(action['payload']))

    // DLITE-3849 — "follow a chip → hyperlink to the row". When a chip is
    // clicked the follow service emits the single clicked entity ref; open
    // (or focus) the panel that OWNS that entity type. The grid inside that
    // panel listens on the same follow$ stream to focus the one row. The
    // listener itself is started (gated) in onUserLogin, where commentary
    // access is known.
    this.followService.follow$
      .pipe(takeUntil(this._destroyed$))
      .subscribe(target => this.onFollowEntity(target))

    this.actions$
      .pipe(takeUntil(this._destroyed$), ofType(openCommentaryDrawer))
      .subscribe(({ payload }) => {
        this.commentaryEntityContext = payload.entityContext
        this.commentaryAction = payload.commentaryAction
        if (payload.commentaryAction === 'add') this.commentaryActionSeq += 1
        this.commentaryDrawerOpen = true
        // Already open and minimized, commentaryDrawerOpen doesn't change, so
        // the frame never sees an input change to un-hide itself on — and
        // commentary-ui only self-restores for a deep link or an add.
        if (this.commentsDrawerFrame?.isMinimized) {
          this.commentsDrawerFrame.restore()
        }
        // DLITE-4110: adopt the context's account as the drawer account so the
        // two stay in sync. The drawer's account-id falls back from the entity
        // context to commentaryAccountId; without this, clicking a bubble sets
        // only the context while commentaryAccountId lingers on the last
        // selection/session account. When the context is later cleared on a
        // cross-panel focus change (DLITE-4032 below), the drawer would then
        // snap back to that stale account. Pointing commentaryAccountId at the
        // context's account keeps the fallback correct (and the activity badge
        // right). Entity rows carry their owning accountId too, so unpinning an
        // entity leaves the drawer on the right account's comments.
        if (payload.entityContext?.accountId != null) {
          // relatedAccountIds is null until fetchSubaccounts resolves; this re-binds it
          // once the fetch lands, so the drawer picks up the rollup shortly after open.
          this.setCommentaryAccount(String(payload.entityContext.accountId))
        }
        // A dispatch carrying neither is unrelated to any in-flight deep
        // link — its own dispatch always carries both, and the widget click
        // already cancels explicitly at its own call site — so cancel here
        // too. Otherwise a grid-bubble/context-menu/smart-suspense open
        // landing inside the pending window would overwrite the ids the
        // link is waiting on without clearing the flag, leaving isRevealed
        // stuck false until the 10s timeout force-closes the drawer.
        if (!payload.threadIds?.length && !payload.commentId) {
          this.cancelPendingCommentaryDeepLink()
        }
        // Must stay after the account switch above, which clears the filter.
        // Unconditional, same reasoning as commentaryThreadIds' own comment:
        // an open path sending none drops a previous value instead of
        // leaving it stale (this is what keeps a resolved deep link's
        // commentId from leaking into a later widget-narrowed open).
        this.commentaryThreadIds = payload.threadIds ?? null
        this.commentaryCommentId = payload.commentId ?? null
        // Bump only when there's an actual slice — see commentaryThreadIdsSeq's
        // own comment for why it needs to change at all; bumping on every
        // open, including ones with no ids, would change it for no reason.
        if (payload.threadIds?.length) this.commentaryThreadIdsSeq += 1
        // DLITE-4032: stamp the panel this context belongs to (the focused
        // panel at open time). A later focus change to a different panel clears
        // it (see onPanelFocused), so a thread created after switching panels
        // links only to the current selection — not the panel opened from.
        this._commentaryContextPanelKey = this._focusedPanelKey
      })

    jobCompletionService.init()
    jobFailureService.init()

    this.store
      .select(selectSessions)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged((a, b) =>
          compareSets(new Set(a.map(x => x.id)), new Set(b.map(x => x.id))),
        ),
      )
      .subscribe(sessions => {
        this.sessionsCount = sessions.length
      })

    this.store
      .select(selectErrorLogSize)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(n => (this.errorLogSize = n))

    this.store
      .select(selectCommentaryActivityCount)
      .pipe(distinctUntilChanged(), takeUntil(this._destroyed$))
      .subscribe(n => (this.commentActivityCount = n))

    this.store
      .select(selectCommandsIsLoading)
      .pipe(distinctUntilChanged())
      .subscribe(isLoading => (this.commandsLoading = isLoading ?? false))

    this.store
      .select(selectIsCreatingSession)
      .pipe(distinctUntilChanged(), takeUntil(this._destroyed$))
      .subscribe(
        isCreatingSession => (this.isCreatingSession = !!isCreatingSession),
      )

    this.sessionsService.loadSessions().catch(error => {
      if (error.status == 403) {
        this.router.navigate([paths.newUser])
      }
    })

    this.sessionsService.isSessionsLoaded
      .pipe(
        filter(isLoaded => isLoaded),
        take(1),
      )
      .subscribe(() => {
        this.activateSessionFromRouter()
      })
  }

  copyToClipboard() {
    this.sessionsService.copySessionToClipboard(
      this.activeSession?.id ?? '',
      true,
    )
  }

  ngOnInit() {
    //watch for updates to query params and adapt
    this.route.queryParamMap
      .pipe(
        take(1),
        tap(params => this.onQueryParamsMapChanged(params)),
      )
      .subscribe()
  }

  ngOnDestroy() {
    this.clearCommentaryDeepLinkTimeout()
    if (this._commentaryDeepLinkToast) {
      this.toastrService.remove(this._commentaryDeepLinkToast.toastId)
    }
    this.heliosHotkeysService.cleanup()
    this.unRegisterGeneralEventsService()
    this.sessionCacheStatusService.cleanup()
    this.sessionCacheTimerService.cleanup()
    this.sessionDataAvailabilityService.cleanup()

    window.removeEventListener(
      'recon-dashboard:Break',
      this._onReconDashboardBreakEvent,
    )

    window.removeEventListener(
      RECON_DASHBOARD_CW_TRANSACTION_EVENT,
      this._onReconDashboardClearwaterTransactionEvent,
    )

    window.removeEventListener(
      RECON_DASHBOARD_COMMENT_THREAD_EVENT,
      this._onReconDashboardCommentThreadEvent,
    )

    window.removeEventListener(
      'commentary-threads-changed',
      this._onCommentaryThreadsChanged,
    )

    // DLITE-5094: commentary refresh is driven by the thread-event socket
    // (CommentaryLiveAccountsService); the drawer's commentary-threads-changed
    // event is only a fallback for accounts the socket isn't live for.
    this.liveAccounts.stop()

    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /**
   * Activates a session from the initial route. On route changes, the router will
   * activate the session. Since location.replaceState is used above,
   * the router events won't be triggered.
   */
  activateSessionFromRouter() {
    this.activatedRoute.params
      .pipe(
        takeUntil(this._destroyed$),
        map(val => val['sessionId']),
        filter(sessionId => sessionId != null),
        mergeMap(sessionId => this.sessionsService.activateSession(sessionId)),
      )
      .subscribe()

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        map(val => ({
          accountIds: val['createFromAccountIds'],
          accountId: val['accountId'],
          reconDate: val['reconDate'],
          loadWindowStartDate: val['loadWindowStartDate'],
          basisName: val['basisName'],
          legacyTranIds: val['legacyTranIds'],
          legacyOrigIds: val['legacyOrigIds'],
        })),
        filter(params => params.accountIds != null || params.accountId != null),
        map(params => ({
          ...params,
          accountIds: params.accountIds
            ? params.accountIds.split(',').map((x: string) => parseInt(x, 10))
            : params.accountId
            ? [parseInt(params.accountId, 10)]
            : [],
          legacyTranIds: params.legacyTranIds
            ? params.legacyTranIds
                .split(',')
                .map((x: string) => parseInt(x, 10))
            : undefined,
          legacyOrigIds: params.legacyOrigIds
            ? params.legacyOrigIds
                .split(',')
                .map((x: string) => parseInt(x, 10))
            : undefined,
        })),
      )
      .subscribe(async params => {
        // Use defaults matching the Create New Session modal
        const reconDate = params.reconDate
          ? getZeroUtcDateAsDate(params.reconDate)
          : new Date() // Default to today
        const loadWindowStartDate = params.loadWindowStartDate
          ? getZeroUtcDateAsDate(params.loadWindowStartDate)
          : periodLengthToDate(PeriodLength.Last100Days) // Default to 100 days back

        const session = await this.sessionsService.create(
          params.accountIds as number[],
          reconDate,
          loadWindowStartDate,
          true,
          params.basisName,
        )

        // Apply transaction filters if provided
        if (session) {
          if (params.legacyTranIds) {
            this.applyLegacyTranIdsFilter(params.legacyTranIds)
          }
          if (params.legacyOrigIds) {
            this.applyLegacyOrigIdsFilter(params.legacyOrigIds)
          }
        }
      })
  }

  /**
   * DLITE-4369 — cold-open the comments drawer on a specific thread/comment
   * from a shared link's commentAccountId/commentThreadId/commentId query
   * params. All three are required together; a partial set means the URL
   * wasn't a comment link (or was mistyped) and is silently ignored — same
   * treatment as any other unrecognized query param.
   */
  activateCommentLinkFromRouter() {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      const accountId = params['commentAccountId']
      const threadId = params['commentThreadId']
      const commentId = params['commentId']
      if (!accountId || !threadId || !commentId) return

      this.openCommentLink({ accountId, threadId, commentId })
      // Found via MR review: navigating with no `queryParams` at all replaces
      // the WHOLE query string, not just the three keys this method reads —
      // a link that also carries activateSessionFromRouter's own params
      // (createFromAccountIds/accountId/etc, gated separately on
      // isSessionsLoaded) could have them wiped out before that method's own
      // take(1) ever reads them, depending on which resolves first. Clearing
      // only these three keys via `merge` leaves any other query params
      // (including a concurrent session-create link) intact.
      void this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          commentAccountId: null,
          commentThreadId: null,
          commentId: null,
        },
        queryParamsHandling: 'merge',
      })
    })
  }

  /**
   * Deliberately does NOT call setCommentaryAccount directly: dispatching
   * openCommentaryDrawer already adopts entityContext.accountId via the
   * existing subscriber (see constructor), exactly as the grid-bubble and
   * context-menu entry points do. Calling it twice would just double the
   * loadCommentaryActivity request.
   */
  openCommentLink({ accountId, threadId, commentId }: CommentLink): void {
    this.clearCommentaryDeepLinkTimeout()
    if (this._commentaryDeepLinkToast) {
      this.toastrService.remove(this._commentaryDeepLinkToast.toastId)
    }
    this.commentaryDeepLinkPending = true
    const token = ++this._commentaryDeepLinkToken
    this._commentaryDeepLinkToast = this.toastrService.info(
      'Opening comment…',
      undefined,
      { disableTimeOut: true },
    )
    this._commentaryDeepLinkTimeout = setTimeout(() => {
      this.handleCommentaryDeepLinkTimeout(token)
    }, AppComponent.COMMENTARY_DEEP_LINK_TIMEOUT_MS)
    this.store.dispatch(
      openCommentaryDrawer({
        entityContext: { accountId },
        commentaryAction: 'view',
        threadIds: [threadId],
        commentId,
      }),
    )
  }

  /**
   * DLITE-4369 — fired by hs-comments-drawer-frame's `deepLinkResolved`
   * output once commentary-ui's own single-thread-open effect settles.
   * Guarded on commentaryDeepLinkPending rather than a dispatch token: a
   * SUPERSEDED dispatch never reaches this at all, because commentary-ui's
   * own staleness guard (lastThreadIdsDispatchKeyRef) already suppresses the
   * event for a dispatch it's moved on from — see CommentaryDrawer.tsx.
   *
   * Found via MR review: `'error'`/`'ignored'` were added on commentary-ui's
   * side after this was first built against the original three-value
   * contract. `'error'` (a transient fetch failure) now gets its own
   * in-drawer overlay with a Try Again control — closing the whole drawer
   * here, the same as `'forbidden'`/`'not-found'`, would throw that control
   * away and force the reader back to the original link. `'ignored'` means
   * the dispatch never reached a single-thread fetch attempt at all (a host
   * contract violation, not a failure), so there is nothing to report
   * either.
   *
   * Found via MR review, second pass: rather than listing every
   * non-failure result by name, close-and-toast only on the two results
   * that actually mean failure. commentary-ui ships as a runtime-loaded
   * bundle, so a newer commentary-ui can reach an already-deployed helios
   * with no helios release in between — this union already grew from three
   * values to five within a single review cycle. An allowlist here would
   * silently drop a future sixth value's real outcome and time out at 10s
   * with a misleading "taking longer than expected" toast; treating
   * anything that isn't a known failure as "reveal, no toast" degrades the
   * same way `reason?` on `CommentaryThreadsChangedDetail` already does for
   * an older React build.
   */
  onCommentaryDeepLinkResolved(detail: { result: CommentaryDeepLinkResult }) {
    if (!this.commentaryDeepLinkPending) return
    this.cancelPendingCommentaryDeepLink()

    if (detail.result !== 'forbidden' && detail.result !== 'not-found') {
      return
    }

    this.onCommentsDrawerClosed()
    this.toastrService.error(
      detail.result === 'forbidden'
        ? "You don't have access to this comment's account."
        : 'This comment or thread is no longer available.',
    )
  }

  /**
   * DLITE-4369 — no commentary-deep-link-resolved event arrived within
   * COMMENTARY_DEEP_LINK_TIMEOUT_MS. Guarded on the dispatch token: unlike
   * the resolved-event path above, a superseded timeout is NOT naturally
   * cancelled by commentary-ui (it's Helios's own setTimeout), so a later
   * dispatch clearing it via clearCommentaryDeepLinkTimeout is the only
   * protection — this check is the backstop in case that ever races.
   */
  private handleCommentaryDeepLinkTimeout(token: number) {
    if (token !== this._commentaryDeepLinkToken) return
    if (!this.commentaryDeepLinkPending) return
    this.cancelPendingCommentaryDeepLink()
    this.onCommentsDrawerClosed()
    this.toastrService.error(
      'This is taking longer than expected. Please try the link again.',
    )
  }

  private clearCommentaryDeepLinkTimeout() {
    if (this._commentaryDeepLinkTimeout != null) {
      clearTimeout(this._commentaryDeepLinkTimeout)
      this._commentaryDeepLinkTimeout = null
    }
  }

  /**
   * DLITE-4369 — cancels any in-flight deep-link resolution (toast + timeout
   * + pending flag) without touching the drawer itself. Idempotent (no-ops
   * once nothing is pending), so it's safe for onCommentaryDeepLinkResolved
   * and handleCommentaryDeepLinkTimeout to also call it before
   * onCommentsDrawerClosed does.
   *
   * Deliberately called ONLY from explicit new-open-or-close gestures
   * (onCommentsHeaderClick, onCommentsDrawerClosed, the recon-dashboard
   * widget-click handler, and the openCommentaryDrawer subscriber for a
   * dispatch unrelated to any deep link) — NOT from clearCommentaryContext()
   * itself. clearCommentaryContext() also fires from
   * ambient events with no user intent behind them at all: a panel-focus
   * change right after a cold-opened deep link's own dispatch (before any
   * workspace panel has focus yet) satisfies onPanelFocused's clear
   * condition on the very first focus event the layout emits — well within
   * the pending window — and an account/session change can do the same. Both
   * used to silently kill the toast and thread/comment ids with no error
   * shown, well before commentary-ui ever got to resolve the link. Found via
   * MR review; see git history for the fix that split this out.
   */
  private cancelPendingCommentaryDeepLink() {
    if (!this.commentaryDeepLinkPending) return
    this.clearCommentaryDeepLinkTimeout()
    if (this._commentaryDeepLinkToast) {
      this.toastrService.remove(this._commentaryDeepLinkToast.toastId)
      this._commentaryDeepLinkToast = null
    }
    this.commentaryDeepLinkPending = false
  }

  /**
   * Apply legacy transaction ID filter to CW Transactions panel
   * @internal
   */
  private applyLegacyTranIdsFilter(legacyTranIds: number[]) {
    if (!legacyTranIds || legacyTranIds.length === 0) return
    this.store.dispatch(setFocusedLegacyTranIds(legacyTranIds))
    this.focusPanel(TransactionsKey)
  }

  /**
   * Apply legacy orig ID filter to CW Transactions panel
   * @internal
   */
  private applyLegacyOrigIdsFilter(legacyOrigIds: number[]) {
    if (!legacyOrigIds || legacyOrigIds.length === 0) return
    this.store.dispatch(setFocusedLegacyOrigIds(legacyOrigIds))
    this.focusPanel(TransactionsKey)
  }

  /** @internal */
  bindHotkeys() {
    this.heliosHotkeysService.bindPanelOpenHotkeys(panelKey =>
      this.focusPanel(panelKey),
    )
    this.heliosHotkeysService.bind('global-layout-lock', () =>
      this.toggleLayoutLocked(),
    )
    this.heliosHotkeysService.bind('toggle-panel-catalog', () => {
      this.toggleHeliosEndNav('PanelCatalog')
    })
    this.heliosHotkeysService.bind('toggle-workspace-catalog', () => {
      this.toggleHeliosEndNav('WorkspaceCatalog')
    })
    this.heliosHotkeysService.bind('global-cheatSheet-help', () =>
      this.hotkeysHelpService.toggleHotkeysHelp(),
    )
    this.heliosHotkeysService.bind('toggle-sessions-drawer', () => {
      this.toggleHeliosEndNav('SessionsDrawer')
    })
  }

  private async initWorkspaces() {
    await this.loaderService.loadStore()
    this.listenerService.listen()
  }

  /** @internal for testing */
  initPreferences() {
    this.preferences.getFocusMode().subscribe(focusMode => {
      this.isFocusMode = focusMode
    })
  }

  async onUserLogin(user: User) {
    if (!user) return
    this.addLoadTimingEvent('userLoginComplete')

    //various services
    loadSecurityDetailsScript(this._document)
    loadSecurityMappingScript(this._document)
    loadRawTradeSummaryScript(this._document)
    loadReconDashboardScript(this._document)

    window.addEventListener(
      'recon-dashboard:Break',
      this._onReconDashboardBreakEvent,
    )

    window.addEventListener(
      RECON_DASHBOARD_CW_TRANSACTION_EVENT,
      this._onReconDashboardClearwaterTransactionEvent,
    )

    window.addEventListener(
      RECON_DASHBOARD_COMMENT_THREAD_EVENT,
      this._onReconDashboardCommentThreadEvent,
    )

    window.addEventListener(
      'commentary-threads-changed',
      this._onCommentaryThreadsChanged,
    )

    loadSmartSuspenseScript(this._document)
    loadOutputManagerScript(this._document)
    loadClarityOpsVisualizerScript(this._document)
    this.messaging.init()

    const userPolicy = await this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this._destroyed$),
        filter((policy): policy is UserPolicy => policy != undefined),
        take(1),
      )
      .toPromise()

    this.addLoadTimingEvent('userPolicyComplete')

    if (userPolicy.accessCopilot) {
      loadCopilot(this._document)
      const copilotHost = addCopilotButton(this.copilot?.nativeElement)
      if (copilotHost) setupCopilotResizing(copilotHost)
      this.copilotService.init()
    }

    // DLITE-3912: remember which account panel(s) the user can access so the
    // commentary chip-follow handler can route account chips by access.
    this.hasAccountsPanelAccess = userPolicy.accessAccountsPanel
    this.hasMyAccountsPanelAccess = userPolicy.accessExternalAccountsPanel

    if (userHasCommentaryAccess(userPolicy)) {
      this.hasAccessCommentary = true
      loadCommentaryScript(this._document)
      // DLITE-3849 — begin listening for commentary chip "follow" events.
      // Idempotent (start() guards re-entry); gated here so only users with
      // commentary access wire the document listener.
      this.followService.start()
      // DLITE-5094 — open the thread-event socket that keeps commentary badges,
      // totals and the activity indicator live. Gated here so users without
      // commentary access never open a socket.
      this.liveAccounts.start()
      // DLITE-4369 — a shared comment link only makes sense once
      // <hs-comments-drawer-frame> exists in the DOM, which is gated on
      // this same hasAccessCommentary flag.
      this.activateCommentLinkFromRouter()
    }

    if (userHasNotificationsAccess(userPolicy)) {
      this.hasAccessNotifications = true
      loadNotificationUiScript(this._document)
    }

    //telemetry depends on user policy
    this.telemetryService.init(user, userPolicy, environment.environment)
    this.usageReportService.init()

    //panels depend on policy
    this.panels = getAvailablePanels(this.isProd, userPolicy).map(p => ({
      ...p,
      hasAccess: true,
    }))
    const externalPanels = getExternalPanels()
    this.panels.push(
      ...externalPanels
        .filter(p => !this.panels.find(x => x.key === p.key))
        .map(p => ({ ...p, hasAccess: false })),
    )
    //index the metadata for quick lookup
    this.panels.forEach(p => this._panelIndex.set(p.key, p))

    //workspaces, hotkeys depend on panels
    this.bindHotkeys()
    this.initPreferences()
    await this.initWorkspaces()
    this.addLoadTimingEvent('initWorkspacesComplete')

    await this._dragSourcesCreatedDfd.getPromise()
    //once we have drag sources and workspaces, we can update the layout
    this.updateLayout()
  }

  async onQueryParamsMapChanged(params: ParamMap) {
    const sessionId = params.get('active-session-id')
    if (sessionId != null) {
      this.router.navigate(['.'], { relativeTo: this.route })
      await this.sessionsService.activateSession(sessionId)
    }
  }

  /**
   * output by LayoutManagerComponent whenever the layout has changed (panels reorganized, resized, etc)
   *
   * @param layout the new layout for LayoutManager
   */
  handleLayoutChanged(layout: LayoutConfig) {
    if (layout) this.store.dispatch(updateActiveLayout(layout))
  }

  toggleLayoutLocked() {
    if (this.layoutManagerComponent != null) {
      if (
        !(
          this.heliosEndNav?.opened &&
          this.heliosEndNavContent === 'PanelCatalog'
        )
      )
        this.lockLayout = !this.lockLayout
      this.updateLayoutLocked()
    }
  }

  private updateLayoutLocked(): void {
    this.layoutLockedSubject.next(
      this.lockLayout &&
        !(
          this.heliosEndNav?.opened &&
          this.heliosEndNavContent === 'PanelCatalog'
        ),
    )
  }

  openErrorLogModal() {
    const compKey = 'error-log'
    const uniqueId = compKey + '-' + Date.now()
    this.modalService.open(ErrorLogModalComponent, {
      size: 'md',
      windowClass: `modal-window-click-through  ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /** @internal */
  async toggleHeliosEndNav(
    heliosEndNavContent: HeliosEndNavContent,
    forceOpen = false,
  ) {
    this.toggleSideNavComplete = false

    if (this.heliosEndNavContent === heliosEndNavContent && !forceOpen) {
      await this.heliosEndNav?.toggle()
      this.toggleSideNavComplete = true
      return
    }

    this.heliosEndNavContent = heliosEndNavContent

    if (forceOpen) {
      await this.heliosEndNav?.open()
      this.lockLayoutDisabled = false
      this.toggleSideNavComplete = true
      return
    }

    // If it's not already open, don't close it
    if (!this.heliosEndNav?.opened) {
      await this.heliosEndNav?.toggle()
    } else {
      this.lockLayoutDisabled = heliosEndNavContent !== 'PanelCatalog'
    }

    // Open ZoomIn widget when Help button is clicked
    if (this.isZoomInHelpActive && heliosEndNavContent) {
      this.openZoomInHelp(heliosEndNavContent)
    }

    // I don't love this, but... it was the only thing to prevent the race condition
    setTimeout(() => (this.toggleSideNavComplete = true), 10)
  }

  closeSideNav() {
    if (!this.toggleSideNavComplete) return
    this.heliosEndNavContent = undefined
    this.heliosEndNav?.close()
  }

  /** Opens the drawer without entity context (header-button entry point). */
  onCommentsHeaderClick() {
    // DLITE-4743 — minimizing hides the drawer with no in-drawer control
    // left to bring it back, so this button is the only way back too. A
    // restore must leave everything else alone: the deep-link/context clear
    // below is for a genuine open/close gesture, and would drop exactly the
    // state minimize exists to preserve.
    if (this.commentaryDrawerOpen && this.commentsDrawerFrame?.isMinimized) {
      this.commentsDrawerFrame.restore()
      return
    }
    // DLITE-4369 — an explicit user gesture, unlike clearCommentaryContext()'s
    // other callers (panel-focus/account/session changes fire ambiently and
    // can arrive well before a cold-opened deep link resolves — cancelling
    // there would silently kill the pending toast with no error shown, see
    // the two other call sites below for the intentional ones).
    this.cancelPendingCommentaryDeepLink()
    this.clearCommentaryContext()
    this.commentaryFollowPin = null
    this.commentaryDrawerOpen = !this.commentaryDrawerOpen
  }

  onCommentsDrawerClosed() {
    this.commentaryDrawerOpen = false
    this.commentaryFollowPin = null
    // DLITE-4369 — see the comment in onCommentsHeaderClick above.
    this.cancelPendingCommentaryDeepLink()
    this.clearCommentaryContext()
  }

  private clearCommentaryContext() {
    // Found via MR review, second pass: moving cancelPendingCommentaryDeepLink
    // into the two gesture methods (onCommentsHeaderClick,
    // onCommentsDrawerClosed) stopped an ambient call here from silently
    // killing the toast/timeout, but this method itself still unconditionally
    // nulled commentaryThreadIds/commentaryCommentId regardless of caller — so
    // the SAME ambient onPanelFocused call (a cold-opened deep link dispatches
    // before any workspace panel has focus, so the layout's first focus event
    // satisfies its clear condition) still wiped the pinned ids before
    // commentary-ui's fetch ever settled. The deep link still couldn't
    // succeed; it just failed loudly via the 10s timeout instead of silently.
    // Guarding the whole method on the pending flag fixes this for real: the
    // two gesture paths and the resolved/timeout paths all call
    // cancelPendingCommentaryDeepLink() BEFORE this, so the flag is already
    // false by the time they get here and clear normally. Trade-off: a
    // genuine account/session switch during the pending window keeps the pin
    // for at most that window — the timeout's own cleanup catches it after.
    if (this.commentaryDeepLinkPending) return
    this.commentaryEntityContext = null
    this.commentaryAction = null
    this._commentaryContextPanelKey = null
    this.commentaryCommentId = null
    this.commentaryThreadIds = null
  }

  /**
   * DLITE-4110: point the comments drawer at `accountId` and refresh the header
   * activity badge for it. Gated on commentary access so tenants without it
   * never trigger the fetch. A null id clears the badge (no account in focus).
   *
   * NOTE: within-account entity counts (`loadCommentaryCounts`) are deliberately
   * NOT loaded here — they follow the SESSION account (wired in the constructor)
   * so selecting a non-session account doesn't blank the session's row counts.
   */
  private setCommentaryAccount(accountId: string | null) {
    // openCommentaryDrawer has no equality check of its own — every comments-cell click,
    // context-menu action, smart-suspense open and deep link routes through here — so
    // reopening on the account already resolved must not clear the rollup and re-resolve it.
    // Doing so drops the attribute for a round trip, and commentary-ui refetches its threads
    // on every related-account-ids change. Captured before commentaryAccountId is reassigned.
    const sameAccount =
      accountId != null && accountId === this.commentaryAccountId
    const alreadyResolved =
      accountId != null &&
      this.commentaryRelatedAccountsResolvedFor === accountId
    // commentary-ui remounts per account, so a leftover filter matches
    // nothing — except while a deep link is pending, matching
    // clearCommentaryContext()'s own guard: an ambient account change (e.g.
    // a session switch reaching this via the merge subscriber below) must
    // not silently un-pin the thread a cold-opened link is still resolving.
    if (!this.commentaryDeepLinkPending) this.commentaryThreadIds = null
    this.commentaryAccountId = accountId
    // DLITE-5094: the drawer's account drives the header activity badge, so it
    // is watched on the thread-event socket under its own owner.
    if (accountId != null) {
      this.liveAccounts.setOwner(this.liveDrawerOwner, 'drawer', [accountId])
    } else {
      this.liveAccounts.releaseOwner(this.liveDrawerOwner)
    }
    this.commentaryFollowPin = null
    if (!sameAccount) {
      this.commentaryRelatedAccountIds = null
      this.commentaryRelatedAccountsResolvedFor = null
    }
    if (!this.hasAccessCommentary) {
      this.commentaryRelatedAccountIds = null
      this.commentaryRelatedAccountsResolvedFor = null
      return
    }
    if (accountId != null) {
      this.store.dispatch(loadCommentaryActivity({ accountId }))
      if (sameAccount && alreadyResolved) return
      // Unconditional otherwise: a plain account just answers 204/empty. Best-effort — a
      // failure leaves relatedAccountIds null.
      this.httpAccountService
        .fetchSubaccounts(Number(accountId))
        .pipe(takeUntil(this._destroyed$))
        .subscribe({
          next: subaccountIds => {
            // Stale-response guard: the user may have switched accounts again before
            // this resolved.
            if (this.commentaryAccountId !== accountId) return
            // Sorted before the truncation below: helios returns a Set, so JSON member order
            // is unspecified and an unsorted slice would roll up a different arbitrary subset
            // of the same aggregate from one open to the next.
            let related = subaccountIds
              .map(id => String(id))
              .filter(id => id !== accountId)
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            if (related.length > MAX_COMMENTARY_RELATED_ACCOUNT_IDS) {
              console.warn(
                `commentary: account ${accountId} has ${related.length} subaccounts; ` +
                  `only rolling up the first ${MAX_COMMENTARY_RELATED_ACCOUNT_IDS}`,
              )
              related = related.slice(0, MAX_COMMENTARY_RELATED_ACCOUNT_IDS)
            }
            this.commentaryRelatedAccountIds =
              related.length > 0 ? JSON.stringify(related) : null
            this.commentaryRelatedAccountsResolvedFor = accountId
          },
          error: () => {
            if (this.commentaryAccountId !== accountId) return
            this.commentaryRelatedAccountIds = null
            this.commentaryRelatedAccountsResolvedFor = null
          },
        })
    } else {
      this.store.dispatch(clearCommentaryActivity())
    }
  }

  /**
   * DLITE-3849 / DLITE-4110 — "follow a chip → hyperlink to the row". Open (or
   * focus) the panel that OWNS the clicked entity type; the grid inside listens
   * on the same follow$ stream (a ReplaySubject) to focus the one row, so it
   * still fires for a panel that mounts/loads afterward.
   *
   * DLITE-4110: comments can now be viewed without a session (from account
   * selection). Transaction and position panels can't display a row without a
   * loaded session, so when one of those chips is followed we first create a
   * session for the thread's account, and account chips start one for the
   * clicked account. break/suspense chips don't need a session.
   */
  private onFollowEntity(target: CommentaryFollowTarget) {
    const keys = panelKeysForEntityType(target.type)
    // The thread's own account, not the drawer's: on an aggregate's rollup the
    // drawer's account is the aggregate, which can't be loaded as a session.
    const sessionAccountId =
      target.type === 'account'
        ? target.id
        : target.threadAccountId ?? this.commentaryAccountId
    if (
      target.type !== 'break' &&
      target.type !== 'suspense' &&
      sessionAccountId != null &&
      this.commentaryAccountId != null &&
      sessionAccountId !== this.commentaryAccountId
    ) {
      this.commentaryFollowPin = sessionAccountId
    }
    if (target.type === 'account') {
      const key = this.hasAccountsPanelAccess
        ? AccountListKey
        : this.hasMyAccountsPanelAccess
        ? ExternalAccountsPanelKey
        : keys[0]
      this.store.dispatch(focusPanelAction(key))
      void this.ensureSessionForCommentaryAccount(sessionAccountId).then(
        () => this.releaseCommentaryFollowPin(sessionAccountId),
        error => this.onFollowSessionFailed(sessionAccountId, error),
      )
      return
    }

    const key = keys[0]
    if (
      target.type === 'transaction' ||
      target.type === 'externalTransaction' ||
      target.type === 'asset'
    ) {
      void this.ensureSessionForCommentaryAccount(sessionAccountId).then(
        loaded => {
          this.releaseCommentaryFollowPin(sessionAccountId)
          if (loaded) this.store.dispatch(focusPanelAction(key))
        },
        error => this.onFollowSessionFailed(sessionAccountId, error),
      )
      return
    }
    this.store.dispatch(focusPanelAction(key))
  }

  private releaseCommentaryFollowPin(accountId: string | null) {
    if (this.commentaryFollowPin === accountId) this.commentaryFollowPin = null
  }

  private onFollowSessionFailed(accountId: string | null, error: unknown) {
    this.releaseCommentaryFollowPin(accountId)
    console.warn(
      `commentary: could not load a session for account ${accountId}`,
      error,
    )
  }

  /**
   * DLITE-4110: ensure a session is loaded for `forAccountId` (default: the
   * account driving the comments drawer), so a followed chip has data to
   * focus. Returns true when a session for that account is (or becomes)
   * active. Mirrors the recon-dashboard transaction-click flow.
   */
  private async ensureSessionForCommentaryAccount(
    forAccountId: string | null = this.commentaryAccountId,
  ): Promise<boolean> {
    // Guard null explicitly: Number(null) is 0 (finite), which would create a
    // bogus session for account 0.
    if (forAccountId == null) return false
    const accountId = Number(forAccountId)
    if (!Number.isFinite(accountId)) return false

    const activeSession = await this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .toPromise()
    if (activeSession?.loadedAccountIds?.includes(accountId)) return true

    const reconDate = await this.store
      .select(selectActualReconDate)
      .pipe(take(1))
      .toPromise()
    const session = await this.sessionsService.create(
      [accountId],
      reconDate ?? new Date(),
      periodLengthToDate(PeriodLength.Last100Days),
      false,
    )
    return !!session
  }

  private readonly _onCommentaryThreadsChanged = (event: Event) => {
    const detail = readCommentaryShadowEvent(
      'commentary-threads-changed',
      event,
    )
    if (!detail) return
    // Permission gate: don't re-fetch counts / activity for a user who isn't
    // permitted to see commentary, even if the drawer somehow dispatched a
    // cross-shadow event.
    if (!this.hasAccessCommentary) return
    // DLITE-5094: when the thread-event socket is live for this account, its
    // own event for this write does the single refresh. This handler is the
    // fallback for accounts the socket isn't live for (not started, not yet
    // open, retries exhausted, denied, or commentary-ws without the socket
    // route), and then behaves exactly as before.
    if (this.liveAccounts.isLive(detail.accountId)) return

    // DLITE-4112: refresh the per-account list total for the changed account so
    // its bubble in the Accounts / My Accounts grids updates in real time. Keyed
    // by accountId and merged (never wholesale-replaced), so it's safe for ANY
    // account — including one that isn't the drawer's or the session's.
    this.store.dispatch(
      loadCommentaryAccountCounts({ accountIds: [detail.accountId] }),
    )

    // Reload the changed account's within-account entity counts so its
    // Transactions / Positions / Breaks rows update in real time — regardless
    // of session or which account the drawer is pointed at. Counts are keyed
    // per-account, so this only ever touches THIS account's sub-map (a late
    // event for another account can't corrupt the displayed one — this replaces
    // MR-318's flat-map stale guard).
    this.store.dispatch(loadCommentaryCounts({ accountId: detail.accountId }))

    // The header activity badge tracks the drawer's account.
    if (detail.accountId === this.commentaryAccountId) {
      this.store.dispatch(
        loadCommentaryActivity({ accountId: detail.accountId }),
      )
    }
  }

  onDragSourcesCreated() {
    /**
     * Whenever a new drag source is loaded all of the components in the layout get recreated.
     * Loading them prior to loading the layout will prevent this behavior.
     */
    this._dragSourcesCreatedDfd.resolve()
  }

  updateLayout() {
    this.store
      .select(selectActiveWorkspace)
      .pipe(
        takeUntil(this._destroyed$),
        filter((workspace): workspace is Workspace => workspace != null),
        distinctUntilChanged((x, y) => x.workspacePrefix === y.workspacePrefix),
        map(workspace => workspace.workspace),
        tap(layout => {
          if (isLayoutLocked(layout as unknown as ResolvedLayoutConfig)) {
            this.lockLayout = true
            this.updateLayoutLocked()
          }
          this.layout = layout
          //setting the layout will stop the spinner.  At this point, we can say the app is usable.
          this.onInitializedComplete()
        }),
      )
      .subscribe()
  }

  /**
   * @internal for testing
   */
  focusPanel(panelKey: string) {
    if (this.layoutManagerComponent?.isPanelAdded(panelKey)) {
      this.layoutManagerComponent.focusPanel(panelKey)
      return
    }

    //open if not already
    const panelMeta = this._panelIndex.get(panelKey)
    if (!panelMeta) {
      throw new Error(`${panelKey} is not an available panel.`)
    }
    this.layoutManagerComponent?.addPanel(panelMeta)
  }

  onPanelFocused(panelKey: string) {
    const panelMeta = this._panelIndex.get(panelKey)
    if (!panelMeta) throw new Error(`${panelKey} is not an available panel.`)

    // DLITE-4032: a focus change to a panel other than the one the open
    // commentary context belongs to means the user moved on — clear it so the
    // next thread links only to the current selection. Re-focusing the
    // context's own panel keeps it. Keyed to the context's owner (not a
    // one-shot flag) so an open that emits no focus event can't strand a guard
    // and skip the next real switch's clear.
    //
    // DLITE-4369, found via MR review (residual, after the pending-flag guard
    // above fixed the silent-clear-during-timeout case): a cold-opened deep
    // link stamps _commentaryContextPanelKey = null, since no panel has focus
    // yet when the link dispatches, before the workspace layout even
    // restores. Once commentaryDeepLinkPending flips false on a successful
    // open, the very next focus event — layout restore, or the user's first
    // click anywhere (layout-manager's focusin re-emit) — satisfied this
    // clear unconditionally, because a null owner is indistinguishable from
    // "belongs to a different panel". A null owner actually means "no panel
    // adopted this context yet", so treat it as an adoption opportunity: the
    // first focus event adopts that panel as the owner, and only a LATER
    // switch away from it clears as before.
    if (this.commentaryEntityContext) {
      if (this._commentaryContextPanelKey === null) {
        this._commentaryContextPanelKey = panelKey
      } else if (panelKey !== this._commentaryContextPanelKey) {
        this.clearCommentaryContext()
      }
    }
    this._focusedPanelKey = panelKey

    this.store.dispatch(
      setFocusedUIComponent({
        name: panelMeta.title,
        key: panelKey,
      }),
    )

    // open ZoomIn Help
    if (this.isZoomInHelpActive) {
      this.openZoomInHelp(panelKey)
    }
  }

  /**
   * Open ZoomIn widget with/without specific panel key
   */
  openZoomInHelp(panelKey: string) {
    // Step 1: Generate the query for loading exact product help in widget
    const query = panelKey
      ? 'csh?cshProduct=' + zoomInProductHelpMappings.get(panelKey)
      : undefined

    // Step 2: Open ZoomIn widget with specific panel query
    iph('open', query)

    // Step 3: since there is no way to check whether widget is open or close
    // so need to hide close button of zoom in widget
    setTimeout(function () {
      const closeBtn = document.getElementsByClassName('zdCloseButton')[0]
      if (closeBtn) {
        ;(closeBtn as unknown as HTMLButtonElement).style.display = 'none'
      }
    }, 300)
  }

  /**
   * Toggle ZoomIn widget from Helios 'Help' Button
   */
  toggleZoomInHelp(): void {
    // show/hide zoom in widget
    this.isZoomInHelpActive ? iph('close') : this.openZoomInHelp('')

    // toggle zoomin widget
    this.isZoomInHelpActive = !this.isZoomInHelpActive
  }

  /**
   * Remove the panel from the lazy-load management system
   */
  stackDestroyed(stackId: string) {
    this.store.dispatch(destroyStack(stackId))
  }

  /**
   * For managing lazy-load panels, this notifies all panel selectors to update whether the panel is 'active' or displaying content to the user.
   * see lazy-load-panel.component.ts
   */
  stackActiveTabUpdate(stackToPanelKey: StackToPanelKey) {
    this.store.dispatch(updateStack(stackToPanelKey))
  }

  /**
   * At this point we consider the app fully initialized
   *
   * @internal for testing
   */
  onInitializedComplete() {
    if (this.isInitialized) return

    //sets the 'initialized' attribute on the div so Cypress tests can continue
    this.isInitialized = true
    this.addLoadTimingEvent('initComplete')

    //now we can send all the timing events, since telemetry is up and running now
    this.loadTimingEvents.forEach(e => {
      this.telemetryService.emitCustomEvent('heliosAppLoadTiming', e)
    })
  }

  /**
   * Measures the time since 'loadStartTime' (set asap in index.html) and adds an entry into our list of load timing events.
   * With this data we can monitor roughly where the time is spent, and we can compare geographies (e.g. is India significantly slower than US?)
   *
   * @param loadStage name of the stage we're at, which will be recorded with the time it took to get here
   */
  private addLoadTimingEvent(loadStage: LoadTimingStage) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initStartTime: number = (window as any)[
      'heliosAppLoadStartTime'
    ] as unknown as number

    if (initStartTime) {
      const durationMs = Date.now() - initStartTime
      // uncomment for local debugging
      // console.log(`initStage: ${loadStage}, duration ${durationMs}`)
      this.loadTimingEvents.push({
        loadStage,
        durationMs,
      })
    }
  }
}
